import { Box, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { grey } from '@mui/material/colors';
import { TextInputRaw } from '@uplift/ui-v2';
import { Fragment, useCallback, useMemo, useState } from 'react';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { Diagnosis } from '@uplift/types';
import { SelectedDiagnosesHashType } from '../../pages/clinical-notes-page/sections/diagnosis/diagnosis.main';
import { Search } from '@mui/icons-material';
import { useL10n } from '@uplift/hooks';

export type DiagnosisSearchProps = {
  diagnoses: Diagnosis[];
  onDiagnosisSelect: (diagnosisId: string) => void;
  selectedDiagnosesHash: SelectedDiagnosesHashType;
  color?: 'primary' | 'secondary';
};

type DiagnosisWithMatchReducerInitialValueType = {
  diagnosis: Diagnosis;
  matches: [number, number][];
  codeMatch: boolean;
}[];

export const DiagnosisSearch: React.VFC<DiagnosisSearchProps> = ({
  diagnoses,
  onDiagnosisSelect,
  selectedDiagnosesHash,
  color = 'primary'
}) => {
  const l10n = useL10n();
  const { classes, cx } = useStyles();
  const [ input, setInput ] = useState('');

  // uses input to search for matches in diagnoses list
  // mostMatches keeps track of highest number of matches per match - used to filter out element matches that are < mostMatches
  const filteredSearchedDiagnoses = useMemo(() => {
    let mostMatches = 0;

    const diagnosesWithMatches = diagnoses.reduce((r, diagnosis) => {
      const matches = match(diagnosis.name, input);
      const codeMatch = !!match(diagnosis.code, input).length;

      mostMatches = Math.max(matches.length, mostMatches);

      return matches.length || codeMatch ? [ ...r, { diagnosis, matches, codeMatch } ] : r;
    }, [] as DiagnosisWithMatchReducerInitialValueType);

    return diagnosesWithMatches.filter(({ matches, codeMatch }) => codeMatch || (matches.length >= mostMatches));
  }, [ input, diagnoses ]);

  const renderHighlight = useCallback((name: string, matches: [number, number][]) => {
    const parts = parse(name, matches);

    return parts.map(({ highlight, text }, i) => (
      <Fragment key={i}>
        {highlight ? <span className={classes.highlight}>{text}</span> : text}
      </Fragment>
    ));
  }, [ classes.highlight ]);

  return (
    <>
      <Box mt={1}>
        <TextInputRaw
          fullWidth
          name="diagnosis-search"
          label={l10n.clinicalNotes.diagnosisSearchTerms}
          onChange={(e) => setInput(e.target.value)}
          value={input}
          setValue={setInput}
          startAdornment={<Search />}
          autoFocus
          autoComplete="off"
          className={classes.inputIconPadding}
          color={color}
        />
      </Box>
      <Box mt={2}>
        <Table>
          <TableBody>
            {filteredSearchedDiagnoses.map(({ diagnosis, matches }) => {
              const isSelected = (diagnosis.id in selectedDiagnosesHash);

              return (
                (
                  <TableRow
                    key={diagnosis.id}
                    className={cx(classes.row,
                      { [classes.selectedRow]: isSelected }
                    )}
                    onClick={!isSelected ? (() => onDiagnosisSelect(diagnosis.id)) : undefined}
                  >
                    <TableCell className={classes.cell}>
                      <Typography>{diagnosis.code}</Typography>
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <Typography>{renderHighlight(diagnosis.name, matches)}</Typography>
                    </TableCell>
                  </TableRow>
                )
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  inputIconPadding: {
    '& input': {
      paddingLeft: theme.spacing(1)
    }
  },
  row: {
    cursor: 'pointer',
    '&:hover': {
      background: grey[100],
    }
  },
  selectedRow: {
    cursor: 'default',
    background: grey[100]
  },
  cell: {
    padding: theme.spacing(0.75),
    '&:nth-of-type(1)': {
      // width and whiteSpace to have cell fit the cell content
      width: '1%',
      whiteSpace: 'nowrap',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  },
  highlight: {
    fontWeight: 600
  }
}));
